import { CheckIcon, ChevronDown, ChevronUp } from '@music-vine/cadence/icons';
import clsx from 'clsx';
import type React from 'react';
import { twMerge } from '@/lib/twMerge';
import { Button, Skeleton } from '@music-vine/cadence/ui';
import { useSettings } from '@/hooks/state/useSettings';
import { useIsClient } from '@/hooks/ui/useIsClient';
import { useSubscriptionButtonState } from '@/hooks/billing/useSubscriptionButtonState';
import type { PricingCardBulletPoint } from '@/components/PricingPage/types';
import type { Settings } from '@/types/settings';
import { getDecimalSeparator } from '@/utils/getDecimalSeparator';
import { HighlightAnnualDiscount } from './HighlightAnnualDiscount';
import type { FeatureSection } from './hooks/usePricingCards';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { getHash } from '../utils';
export type ButtonType = 'manage' | 'upgrade' | 'start' | 'disabled' | 'free';
export interface PricingCardProps {
  /** Label of the floating pill on the highlighted card. */
  badgeLabel?: string;
  className?: string;
  currency: Settings['currency'];
  /**
   * For statically generated pages, where the server can't know the visitor's
   * currency: renders the price skeleton until mounted rather than briefly
   * showing the build default. Pages resolving currency per request (/pricing)
   * leave this off and paint real prices immediately.
   */
  deferPriceUntilMounted?: boolean;
  description?: string;
  featureSections: FeatureSection[];
  highlightAnnualDiscount?: boolean;
  isPopular?: boolean;
  isPriceLoading?: boolean;
  period: string;
  price: number | string;
  priceNote?: string;
  subtitle: string;
  subtitleIcon?: React.ReactNode;
  title: string;
  type: 'Free' | 'Essentials' | 'Creator' | 'Pro' | 'Business';
}

// Generate a stable ID from feature content
const getFeatureId = (content: string | React.ReactElement, type: string, prefix: string, index: number): string => {
  const safeContent = typeof content === 'string' ? content : 'element';
  const contentStr = `${type}-${prefix}-${index}-${safeContent}`;
  return getHash(contentStr);
};

// Reusable FeatureList component
const FeatureList: React.FC<{
  sectionTitle: string;
  items: PricingCardBulletPoint[];
  type: string;
  prefix: string;
  className?: string;
}> = ({
  sectionTitle,
  items,
  type,
  prefix,
  className
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  if (!items || items.length === 0) {
    return null;
  }
  return <div className={className} data-sentry-component="FeatureList" data-sentry-source-file="PricingCard.tsx">
      <p className="mb-2 font-sans font-semibold text-gray-950 text-sm dark:text-white">
        {sectionTitle}:
      </p>
      <ul className="flex flex-col gap-2 text-gray-700 text-sm dark:text-gray-400">
        {items.map((item, index) => {
        const featureId = getFeatureId(item.content, type, prefix, index);
        const hasTooltip = Boolean(item.tooltip?.trim());
        const isOpen = expandedItems.has(featureId);
        return <li className="flex items-start" key={featureId}>
              <span className={clsx('mr-2 will-change-transform', tierTheme(type).check)}>
                <CheckIcon className="h-4 w-4" />
              </span>
              <div className="text-balance will-change-transform">
                {!hasTooltip && <span>{item.content}</span>}
                {hasTooltip && <>
                    <Button className={cn(
              // Undoes Cadence's nowrap/centre, which let a long label widen the card.
              '-ml-2 flex h-auto items-center justify-start whitespace-normal p-0 px-2 text-left font-normal text-gray-700 text-sm active:scale-100 dark:text-gray-400')} onClick={() => setExpandedItems(prev => {
                const newSet = new Set(prev);
                if (newSet.has(featureId)) {
                  newSet.delete(featureId);
                } else {
                  newSet.add(featureId);
                }
                return newSet;
              })} variant="transparent">
                      <span>{item.content}</span>
                      {isOpen ? <ChevronUp className="size-4 shrink-0" /> : <ChevronDown className="size-4 shrink-0" />}
                    </Button>

                    {!!isOpen && <div className="flex flex-row flex-wrap items-start gap-x-8 gap-y-2 pt-2 text-gray-700 text-xs dark:text-gray-400">
                        {item.tooltip}
                      </div>}
                  </>}
              </div>
            </li>;
      })}
      </ul>
    </div>;
};

/**
 * Per-tier colour treatment. Everything not listed here takes `default` (the
 * pink scale); Free and Business are the two tiers that break from it.
 * `cta`/`ctaManage` are only applied where the card opts into an outlined
 * button — a solid blue Manage CTA wasn't worth its own Cadence variant.
 */
const TIER_THEME = {
  Free: {
    card: 'border-gray-200 dark:border-gray-800',
    check: 'text-gray-400',
    cta: 'hover:bg-gray-100',
    ctaManage: 'hover:bg-gray-100',
    header: 'bg-gray-50 dark:bg-gray-900'
  },
  Business: {
    card: 'border-blue-business-200 dark:border-blue-business-800',
    check: 'text-blue-business-700 dark:text-blue-400',
    cta: 'border-blue-business-200 hover:bg-blue-business-75 dark:border-blue-business-800 dark:hover:bg-blue-business-75',
    ctaManage: 'border-blue-business-600 bg-blue-business-600 text-white hover:border-blue-business-700 hover:bg-blue-business-700',
    header: 'bg-blue-business-25 dark:bg-blue-business-975'
  },
  default: {
    card: 'border-pink-200 dark:border-red-950/70',
    check: 'text-pink-500',
    cta: 'border-pink-200 hover:bg-pink-100 dark:border-gray-800',
    ctaManage: 'border-pink-200 text-brand-primary hover:bg-pink-100 dark:border-gray-800',
    header: 'bg-pink-50 dark:bg-red-950/20'
  }
} as const;
const tierTheme = (type: string) => TIER_THEME[type as 'Business' | 'Free'] ?? TIER_THEME.default;

// Price Display Component using Grid
const PriceDisplay: React.FC<{
  currency?: Settings['currency'];
  price: number | string;
  period: string;
}> = ({
  currency,
  price,
  period
}) => {
  const priceStr = String(price);
  const parts = priceStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] && parts[1].trim() !== '' ? parts[1] : null;

  // Rendered rather than implied by the superscript styling: without a real
  // separator in the DOM the price reads "£719 /month" to screen readers and
  // crawlers, and Google's snippet quoted the plans at 100x (DEV-6824).
  const decimals = decimalPart ? `${getDecimalSeparator(currency?.code ?? 'USD')}${decimalPart}` : null;
  const currencySymbol = currency?.symbol_left ?? '';
  const isCanadian = currency?.code === 'CAD';
  const priceHasValue = priceStr !== '0';

  // Business quotes "/month excl. tax": the unit carries the price's colour,
  // the qualifier after it stays muted.
  const [periodUnit, ...periodQualifier] = period.split(' ');
  return <div className="inline-grid grid-cols-[auto_auto] grid-rows-[auto_auto] items-baseline font-bold font-sans dark:text-white" key={priceStr} data-sentry-component="PriceDisplay" data-sentry-source-file="PricingCard.tsx">
      {/* Integer Part */}
      <span className="row-span-2 self-center text-5xl leading-[0.74]" key={`int-${integerPart}`} suppressHydrationWarning>
        <span className="pr-[0.15rem]">{`${currencySymbol}`}</span>
        <span className="tracking-wide">{`${integerPart}`}</span>
      </span>
      {/* Decimal Part (Conditional) */}
      <span className="col-start-2 row-start-1 h-5 text-xl leading-[0.74]" key={`dec-${decimalPart}`} suppressHydrationWarning>
        {decimals}
        {isCanadian && priceHasValue && ' CAD'}
      </span>
      {/* Period Part */}
      <span className="col-start-2 row-start-2 self-end font-normal font-sans text-gray-950 text-sm leading-none dark:text-white" key={`period-${period}`} suppressHydrationWarning>
        {periodUnit}
        {periodQualifier.length > 0 && <span className="text-gray-700 dark:text-gray-500">
            {' '}
            {periodQualifier.join(' ')}
          </span>}
      </span>
    </div>;
};
export const PricingCard: React.FC<PricingCardProps> = ({
  type,
  title,
  subtitle,
  subtitleIcon,
  badgeLabel,
  description,
  price,
  period,
  priceNote,
  isPopular = false,
  featureSections,
  currency: serverCurrency,
  className,
  highlightAnnualDiscount = true,
  isPriceLoading = false,
  deferPriceUntilMounted = false
}) => {
  const {
    settings,
    isLoading
  } = useSettings();
  const isMounted = useIsClient();
  const theme = tierTheme(type);
  const hasSubtitleIcon = Boolean(subtitleIcon);
  const buttonStates = useSubscriptionButtonState({
    plans: [{
      type
    }]
  });
  const buttonState = buttonStates[0];

  // Server currency until mounted: cached settings resolve before the first
  // client render, so a "£" price hydrating against "$" in static HTML is a
  // text mismatch, and React 19 recovers by re-rendering the page (DEV-6494).
  const currency = isMounted && !isLoading ? settings?.currency : serverCurrency;
  return <div className={twMerge(
  // Subgrids the plan grid's rows so every card's price section is the
  // same height. Keep `@container` off this element — containment
  // disqualifies it from being a subgrid. gap-y-0 because a subgrid
  // inherits the parent's gutters.
  'relative row-span-2 mx-auto grid h-full w-full grid-rows-subgrid gap-y-0 rounded-lg border border-solid transition-all duration-200 motion-reduce:transform-none motion-reduce:transition-none lg:hover:-translate-y-1.5 xl:max-w-96', theme.card, isPopular && 'border border-pink-500 dark:border-pink-500', className)} data-sentry-component="PricingCard" data-sentry-source-file="PricingCard.tsx">
      {!!isPopular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform whitespace-nowrap rounded-full bg-pink-500 px-3 py-1 font-semibold text-base text-white">
          {badgeLabel ?? 'POPULAR'}
        </div>}

      <div className={twMerge('@container flex flex-col overflow-hidden rounded-t-lg px-4 pt-5 pb-7 font-sans md:px-5', theme.header)}>
        <div className="mb-4">
          <h3 className="fl-text-xl/2xl mb-1 font-sans font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <div className="flex items-center text-gray-900 dark:text-white">
            {hasSubtitleIcon && <span className="mr-1.5 flex translate-y-px items-center self-baseline">
                {subtitleIcon}
              </span>}
            {/* Was `@xs:text-sm text-xs`, but the header container never
                reaches 20rem at any breakpoint, so it only ever rendered
                xs (DEV-6728). */}
            <p className="text-pretty text-gray-900 text-sm dark:text-white">
              {subtitle}
            </p>
          </div>
        </div>

        {description && <p className="mb-4 @xs:min-h-[2lh] min-h-[3lh] text-pretty text-gray-700 text-sm md:text-sm dark:text-gray-400">
            {description}
          </p>}

        <div className="font-heading">
          {isPriceLoading || deferPriceUntilMounted && !isMounted ? <Skeleton className="h-9 w-32" data-testid="pricing-card-price-skeleton" /> : <PriceDisplay currency={currency} period={period} price={price} />}
        </div>

        {/* One line under the price: the yearly saving. Always rendered so the
            CTA keeps its gap on the cards that have none. */}
        <div className="mb-6 text-pretty pt-3 font-sans text-gray-700 text-sm dark:text-white">
          {priceNote ?? (highlightAnnualDiscount && <HighlightAnnualDiscount serverCurrency={serverCurrency} type={type} />)}
        </div>

        <Button
      // Free is the one outlined CTA that stays borderless.
      border={!(isPopular || type === 'Free')} className={clsx('mt-auto w-full font-sans font-semibold', !isPopular && ['disabled:opacity-60', buttonState.buttonType === 'manage' ? theme.ctaManage : theme.cta])} disabled={buttonState.isDisabled} onClick={buttonState.onClick} size="lg" type="button" variant={isPopular ? 'brand' : 'contrast'} data-sentry-element="Button" data-sentry-source-file="PricingCard.tsx">
          {buttonState.buttonText}
        </Button>
      </div>

      <div className="flex flex-col gap-4 p-5 pb-8 font-sans">
        {(featureSections ?? []).map(section => <FeatureList items={section.items} key={`${type}-section-${section.title.replace(/\s+/g, '-').toLowerCase()}`} prefix={section.title.replace(/\s+/g, '-').toLowerCase()} sectionTitle={section.title} type={type} />)}
      </div>
    </div>;
};